import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { VerifiableEntityType } from '../VerifiedTick/entityTypes';

/**
 * F09 v2 — app-level wiring for the blue tick.
 *
 * An interactive `<VerifiedTick>` links to the "How verification works"
 * explainer. Apps mount this provider once to (a) point the link at their own
 * route and (b) render it with their router's link component. Without a
 * provider the defaults apply: `/verified` (or `/verified#<type>`) rendered as
 * a plain `<a>`. Returning `null` from `explainerHref` makes every tick inert
 * (the admin app mounts `explainerHref: () => null`).
 */

export interface VerifiedTickLinkRenderProps {
  readonly href: string;
  readonly children: ReactNode;
  /** Accessible name for the link (the localized tooltip text). */
  readonly ariaLabel: string;
}

export type VerifiedTickExplainerHref = (type: VerifiableEntityType | undefined) => string | null;
export type VerifiedTickRenderLink = (props: VerifiedTickLinkRenderProps) => ReactNode;

export interface VerifiedTickConfig {
  readonly explainerHref?: VerifiedTickExplainerHref | undefined;
  readonly renderLink?: VerifiedTickRenderLink | undefined;
}

/** The config every tick reads — both fields always resolved. */
export interface ResolvedVerifiedTickConfig {
  readonly explainerHref: VerifiedTickExplainerHref;
  readonly renderLink: VerifiedTickRenderLink;
}

export const defaultVerifiedTickExplainerHref: VerifiedTickExplainerHref = (type) =>
  type ? `/verified#${type}` : '/verified';

export const defaultVerifiedTickRenderLink: VerifiedTickRenderLink = ({
  href,
  children,
  ariaLabel,
}) => (
  <a
    href={href}
    aria-label={ariaLabel}
    className="inline-flex items-center rounded-sm align-middle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/40"
  >
    {children}
  </a>
);

const DEFAULT_CONFIG: ResolvedVerifiedTickConfig = {
  explainerHref: defaultVerifiedTickExplainerHref,
  renderLink: defaultVerifiedTickRenderLink,
};

const VerifiedTickContext = createContext<ResolvedVerifiedTickConfig>(DEFAULT_CONFIG);

export interface VerifiedTickProviderProps extends VerifiedTickConfig {
  readonly children: ReactNode;
}

/**
 * Provide tick link behaviour to the subtree. Fields left unset inherit from
 * the nearest parent provider (or the defaults), so a nested provider can
 * override just one of them.
 */
export function VerifiedTickProvider({
  explainerHref,
  renderLink,
  children,
}: VerifiedTickProviderProps) {
  const parent = useContext(VerifiedTickContext);
  const value = useMemo<ResolvedVerifiedTickConfig>(
    () => ({
      explainerHref: explainerHref ?? parent.explainerHref,
      renderLink: renderLink ?? parent.renderLink,
    }),
    [explainerHref, renderLink, parent],
  );
  return <VerifiedTickContext.Provider value={value}>{children}</VerifiedTickContext.Provider>;
}

/** Read the resolved tick config (defaults when no provider is mounted). */
export function useVerifiedTickConfig(): ResolvedVerifiedTickConfig {
  return useContext(VerifiedTickContext);
}
